import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import { BModal } from 'bootstrap-vue';
import { Component, Inject, Ref, Prop } from 'vue-property-decorator';
import AlertService from '@/shared/alert/alert.service';
import * as dateUtils from '@/shared/date/date-utils';
import { mixins } from 'vue-class-component';
import DiaMixin from '@/shared/date/dia-utils';
import SolicitudService from '@/entities/solicitud/solicitud.service';
import { Solicitud } from '@/shared/model/solicitud.model';
import { ICronograma } from '@/shared/model/cronogramaActividades.model';
import { Solucion } from '@/shared/model/solucion.model';
import { parseISO } from 'date-fns';
import dayjs from 'dayjs';

const VALIDATIONS = function () {
  return {
    descripcionActividad: {
      required,
      minLength: minLength(10),
      maxLength: maxLength(500),
    },
    fechaInicio: {
      required,
    },
    fechaFin: {
      required,
    },
  };
};

@Component({
  validations: VALIDATIONS,
})
export default class CronogramaActividadesComponent extends mixins(DiaMixin) {
  @Inject('alertService') private alertService: () => AlertService;
  @Inject('solicitudService') private solicitudService: () => SolicitudService;

  @Ref('confirmacionModal')
  public readonly confirmacionModal: BModal;

  @Ref('periodoModal')
  public readonly periodoModal: BModal;

  @Ref('eliminaActividadModal')
  public readonly eliminaActividadModal: BModal;

  @Prop({ required: false })
  public solicitud: Solicitud;

  @Prop({ required: true })
  public readonly solucion: Solucion;

  public isFetching = false;
  form = this.solicitudModel;

  public totalItems = 0;
  public isSearching = false;
  public numeroMax = 0;
  public formatoDatePicker = dateUtils.FORMATO_DATE_PICKER;
  public mensajeCarga: string = null;
  public indicadorCargaShow = false;
  public accion: string = null;
  public fechaInicio: Date | null = null;
  public fechaFin: Date | null = null;
  public fechaInicioMovilidad: Date;
  public fechaFinMovilidad: Date;
  public tituloModal: string = null;
  public mensajeConfirmacion: string = null;
  public descripcionActividad: string = null;
  public solicitudDetalle: Solicitud = new Solicitud();
  public numero: number;
  public cronogramaActividades: ICronograma[];
  public pasosPreviosMovilidad = false;
  public pasosPreviosEspMed = false;

  private get solicitudModel() {
    return {
      solicitud: {
        beca: {
          PeriodoBeca: {
            fechaInicioBeca: '',
            fechaFinnBeca: '',
          },
        },
        fechaInicioMovilidad: '',
        fechaFinMovilidad: '',
        estadoActual: '',
      },
      tags: [],
    };
  }

  public readonly fields = [
    {
      key: 'numero',
      label: this.$t('apeironGwApp.cronogramaActividades.grid.numeroActividad'),
      class: 'text-left',
      thStyle: { width: '5rem' },
    },
    {
      key: 'fechaInicio',
      label: this.$t('apeironGwApp.cronogramaActividades.grid.fechaInicio'),
      class: 'text-left',
      thStyle: { width: '10rem' },
      formatter: dateUtils.defaultFormat,
    },
    {
      key: 'fechaFin',
      label: this.$t('apeironGwApp.cronogramaActividades.grid.fechaFin'),
      class: 'text-left',
      thStyle: { width: '10rem' },
      formatter: dateUtils.defaultFormat,
    },
    {
      key: 'descripcionActividad',
      label: this.$t('apeironGwApp.cronogramaActividades.grid.descripcionActividad'),
      class: 'text-left',
      thStyle: { width: '20rem' },
    },
  ];

  public created(): void {
    if (this.form.tags) {
      this.form.tags = this.solucion.tags;
    }
    this.pasosPreviosEspMed = !!this.solicitud.beca?.id && !!this.solicitud.fechaInicioMovilidad && !!this.solicitud.institucion?.id;
    this.pasosPreviosMovilidad =
      !!this.solicitud.beca?.id &&
      !!this.solicitud.fechaInicioMovilidad &&
      !!this.solicitud.apoyoSolicitado?.apoyo &&
      this.solicitud.institucion?.id;
  }

  public isCapturable(): boolean {
    const tags = this.solucion.tags;
    if (tags.includes('MovilidadNacional')) {
      return this.pasosPreviosMovilidad;
    }
    if (tags.includes('MovilidadEspecialidadMedica')) {
      return this.pasosPreviosEspMed;
    }
    return false;
  }

  public guardarPeriodo() {
    this.confirmacionModal.hide();
    const actividad = {
      numero: (this.solicitud.cronogramaActividades?.length ?? 0) + 1,
      descripcionActividad: this.descripcionActividad,
      fechaInicio: this.fechaInicio ? this.formatLocalDate(this.fechaInicio) : '',
      fechaFin: this.fechaFin ? this.formatLocalDate(this.fechaFin) : '',
    };
    if (!this.solicitud.cronogramaActividades) {
      this.solicitud.cronogramaActividades = [];
    }
    this.solicitud.cronogramaActividades.push(actividad);
    this.mensajeCarga = this.$t('indicadorCarga.guardando', {
      mensaje: this.$t('apeironGwApp.cronogramaActividades.mensajes.actividad'),
    }).toString();
    this.indicadorCargaShow = true;
    this.solicitudService()
      .update(this.solicitud)
      .then(res => {
        this.solicitud = { ...res };
        this.alertService().showSuccess(this, this.$t('apeironGwApp.cronogramaActividades.mensajes.guardadoExitoso').toString());
        this.cerrarModal();
      })
      .catch(error => {
        this.alertService().showHttpError(this, error.response);
      })
      .finally(() => {
        this.indicadorCargaShow = false;
      });
  }

  public eliminarPeriodo(): void {
    if (this.solicitud.cronogramaActividades.length > 0) {
      this.solicitud.cronogramaActividades.pop();
      this.solicitudService()
        .update(this.solicitud)
        .then(res => {
          this.solicitud = { ...res };
          this.alertService().showSuccess(this, 'apeironGwApp.cronogramaActividades.mensajes.ultimaActividad');
          this.eliminaActividadModal.hide();
        })
        .catch(error => {
          this.alertService().showHttpError(this, error.response);
        });
    }
  }

  public abrirModalEliminar() {
    this.eliminaActividadModal.show();
    this.accion = 'E';
    this.tituloModal = this.$t('apeironGwApp.cronogramaActividades.modal.title.eliminar').toString();
  }

  /**
   * Inicializa y muestra el modal para agregar una nueva actividad
   * al cronograma.
   * @returns void
   */
  public abrirModalAgregar(): void {
    this.mensajeCarga = this.$t('indicadorCarga.cargando', {
      mensaje: this.$t('apeironGwApp.cronogramaActividades.mensajes.pantalla'),
    }).toString();
    this.indicadorCargaShow = true;
    this.accion = 'A';
    this.descripcionActividad = '';
    const ultimaFechaRegistro = this.obtieneUltimaFechaFin();
    this.fechaInicio = ultimaFechaRegistro ? ultimaFechaRegistro : parseISO(this.solicitud.fechaInicioMovilidad);
    this.fechaFin = null;
    this.tituloModal = this.$t('apeironGwApp.cronogramaActividades.modal.title.agregar').toString();
    this.periodoModal.show();
    this.indicadorCargaShow = false;
  }

  public cerrarModal() {
    this.periodoModal.hide();
    this.accion = null;
  }

  public abrirModalConfirmacion() {
    if (this.$v.descripcionActividad.$invalid && 'E' !== this.accion) {
      this.$v.descripcionActividad.$touch();
      if (this.$v.fechaInicio.$invalid) {
        this.$v.fechaInicio.$touch();
      }
      if (this.$v.fechaFin.$invalid) {
        this.$v.fechaFin.$touch();
      }
    } else if (this.validarFechas()) {
      this.confirmacionModal.show();
    }
  }

  public cerrarModalConfirmacion() {
    this.confirmacionModal.hide();
  }

  public cerrarModalConfirmacionEliminar() {
    this.eliminaActividadModal.hide();
  }

  public get buttonText() {
    switch (this.accion) {
      case 'A':
        return this.$t('apeironGwApp.cronogramaActividades.modal.boton.guardar');
      default:
        return null;
    }
  }

  public get buttonIcon() {
    switch (this.accion) {
      case 'A':
        return 'icon-guardar';
      default:
        return null;
    }
  }

  /**
   * Valida que las fechas de la actividad cumplan las reglas de negocio:
   *
   * Reglas aplicadas:
   * - Si la acción (`accion`) es 'E', no se realiza validación y retorna `true`.
   * - La fecha de inicio no puede ser mayor que la fecha de fin.
   * - La fecha de inicio no puede ser menor que la fecha de inicio de movilidad.
   * - La fecha de fin no puede ser mayor que la fecha de fin de movilidad.
   * - No debe existir traslape con otras actividades del cronograma.
   *
   * Consideraciones técnicas:
   * - Todas las fechas se normalizan a timestamp UTC (00:00:00.000)
   *   para evitar problemas de zona horaria.
   * - Si el cronograma no tiene actividades, no se valida traslape.
   * - Si ocurre un error de validación, se muestra un mensaje usando `alertService`
   *   y se retorna `false`.
   *
   * @returns `true` si las fechas son válidas; `false` en caso contrario.
   */
  private validarFechas() {
    if ('E' === this.accion) {
      return true;
    }

    const toLocalDateTimestamp = (fecha: Date | string) => {
      let date: Date;

      if (typeof fecha === 'string') {
        date = this.parseLocalDate(fecha);
      } else {
        date = fecha;
      }

      return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
    };

    const nuevaFechaInicio = toLocalDateTimestamp(this.fechaInicio);
    const nuevaFechaFin = toLocalDateTimestamp(this.fechaFin);
    const fechaInicioMovilidad = toLocalDateTimestamp(this.solicitud.fechaInicioMovilidad);
    const fechaFinMovilidad = toLocalDateTimestamp(this.solicitud.fechaFinMovilidad);

    if (nuevaFechaInicio > nuevaFechaFin) {
      this.alertService().showError(this, this.$t('apeironGwApp.cronogramaActividades.label.fechaInicioActividad').toString());
      return false;
    }

    if (nuevaFechaInicio < fechaInicioMovilidad) {
      this.alertService().showError(this, this.$t('apeironGwApp.cronogramaActividades.label.fechaMenorMovilidad').toString());
      return false;
    }

    if (nuevaFechaFin > fechaFinMovilidad) {
      this.alertService().showError(this, this.$t('apeironGwApp.cronogramaActividades.label.fechaMayorMovilidad').toString());
      return false;
    }

    const cronogramaActividades = this.solicitud.cronogramaActividades;
    // Si no hay cronograma aún, no hay traslapes
    if (!cronogramaActividades?.length) {
      return true;
    }

    // Validar traslapes
    for (const actividad of cronogramaActividades) {
      if (!actividad?.fechaInicio || !actividad?.fechaFin) continue;
      const actInicio = toLocalDateTimestamp(actividad.fechaInicio);
      const actFin = toLocalDateTimestamp(actividad.fechaFin);

      const hayTraslape = nuevaFechaInicio <= actFin && nuevaFechaFin >= actInicio;

      if (hayTraslape) {
        this.alertService().showError(
          this,
          this.$t('apeironGwApp.cronogramaActividades.label.fechaTraslape', {
            numero: actividad.numero ?? '',
          }).toString()
        );
        return false;
      }
    }
    return true;
  }

  public aceptarConfirmacion() {
    switch (this.accion) {
      case 'A':
        this.guardarPeriodo();
        break;
      default:
        break;
    }
  }

  /**
   * Busca la solicitud.
   */
  public buscarSolucitud() {
    this.isFetching = true;
    this.solicitudService()
      .find(this.solicitud.id)
      .then(
        res => {
          this.form.solicitud = { ...res, ...JSON.parse(JSON.stringify(res)) };
          this.isFetching = false;
        },
        err => {
          this.isFetching = false;
        }
      );
  }

  /**
   * Obtiene la última fecha de finalización del cronograma y retorna
   * una nueva instancia de Date con un día adicional.
   *
   * - Si el cronograma no existe o está vacío, retorna `undefined`.
   * - No modifica las fechas originales del cronograma.
   *
   * @returns La fecha más alta de `fechaFin` más un día, o `undefined`
   * si no hay actividades.
   */
  private obtieneUltimaFechaFin(): Date {
    const cronograma = this.mapToDates();
    if (!cronograma?.length) return;
    const ordenado = [...cronograma].sort((a, b) => {
      return a.fechaFin.getTime() - b.fechaFin.getTime();
    });
    const fechaFinMasAlta = ordenado[ordenado.length - 1].fechaFin;
    const fechaNueva = new Date(fechaFinMasAlta.getTime());
    // agregar 1 día sin afectar el original
    fechaNueva.setDate(fechaNueva.getDate() + 1);
    return fechaNueva;
  }

  /**
   * Mapea el cronograma de actividades convirtiendo las propiedades
   * `fechaInicio` y `fechaFin` a objetos `Date` cuando son strings.
   *
   * - Si la fecha es un string, se convierte usando `parseLocalDate`.
   * - Si ya es un objeto `Date`, se conserva.
   * - Si es `null` o `undefined`, se retorna como `null`.
   *
   * @returns Un nuevo arreglo de actividades con las fechas normalizadas a `Date`.
   */
  mapToDates() {
    return (this.solicitud?.cronogramaActividades ?? []).map(item => ({
      ...item,
      fechaInicio: typeof item.fechaInicio === 'string' ? this.parseLocalDate(item.fechaInicio) : item.fechaInicio ?? null,
      fechaFin: typeof item.fechaFin === 'string' ? this.parseLocalDate(item.fechaFin) : item.fechaFin ?? null,
    }));
  }

  /**
   * Convierte una fecha en formato `YYYY-MM-DD` a un objeto `Date`
   * ajustado a la zona horaria local.
   *
   * - Crea inicialmente la fecha en UTC.
   * - Ajusta los minutos según el `timezoneOffset` para evitar
   *   desfases por zona horaria.
   *
   * @param dateString - Fecha en formato `YYYY-MM-DD`.
   * @returns Instancia de `Date` ajustada a la zona horaria local.
   */
  private parseLocalDate(dateString: string): Date {
    const [year, month, day] = dateString.split('-').map(Number);

    return new Date(year, month - 1, day);
  }

  private formatLocalDate(value: any): string {
    if (!value) return '';

    let date: Date;

    if (value instanceof Date) {
      date = value;
    } else if (typeof value === 'string') {
      const [year, month, day] = value.split('-').map(Number);
      date = new Date(year, month - 1, day);
    } else if (dayjs.isDayjs(value)) {
      date = value.toDate();
    } else {
      date = new Date(value);
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }
}
